import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  ActivateLoyaltyProgramResponse,
  BulkGetLoyaltyProgramResponse,
  DisablePointsExpirationResponse,
  EnablePointsExpirationResponse,
  GetLoyaltyProgramPremiumFeaturesResponse,
  GetLoyaltyProgramResponse,
  LoyaltyProgram,
  PauseLoyaltyProgramResponse,
  ProgramUpdatedEnvelope,
  UpdateLoyaltyProgramResponse,
  activateLoyaltyProgram as universalActivateLoyaltyProgram,
  bulkGetLoyaltyProgram as universalBulkGetLoyaltyProgram,
  disablePointsExpiration as universalDisablePointsExpiration,
  enablePointsExpiration as universalEnablePointsExpiration,
  getLoyaltyProgram as universalGetLoyaltyProgram,
  getLoyaltyProgramPremiumFeatures as universalGetLoyaltyProgramPremiumFeatures,
  pauseLoyaltyProgram as universalPauseLoyaltyProgram,
  updateLoyaltyProgram as universalUpdateLoyaltyProgram,
} from './loyalty-v1-program-programs.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/loyalty' };

export function getLoyaltyProgram(
  httpClient: HttpClient
): GetLoyaltyProgramSignature {
  return () =>
    universalGetLoyaltyProgram(
      // @ts-ignore
      { httpClient }
    );
}

interface GetLoyaltyProgramSignature {
  /**
   * Retrieves the loyalty program.
   */
  (): Promise<
    NonNullablePaths<
      GetLoyaltyProgramResponse,
      | `loyaltyProgram.status`
      | `loyaltyProgram.pointsExpiration.status`
      | `loyaltyProgram.pointsExpiration.monthsOfInactivity`
      | `loyaltyProgram.pointsExpiration.expiringPointsPercentage`
      | `loyaltyProgram.premiumFeatures.loyaltyProgram`
      | `loyaltyProgram.premiumFeatures.tiers`
      | `loyaltyProgram.premiumFeatures.pointsExpiration`,
      4
    >
  >;
}

export function bulkGetLoyaltyProgram(
  httpClient: HttpClient
): BulkGetLoyaltyProgramSignature {
  return () =>
    universalBulkGetLoyaltyProgram(
      // @ts-ignore
      { httpClient }
    );
}

interface BulkGetLoyaltyProgramSignature {
  /**
   * Retrieves loyalty programs for all metasites that the caller is a member of.
   *
   * Must be called with user identity.
   */
  (): Promise<
    NonNullablePaths<
      BulkGetLoyaltyProgramResponse,
      | `programInSites`
      | `programInSites.${number}.metaSiteId`
      | `programInSites.${number}.loyaltyProgram.status`
      | `programInSites.${number}.loyaltyProgram.pointsExpiration.status`
      | `programInSites.${number}.loyaltyProgram.pointsExpiration.monthsOfInactivity`
      | `programInSites.${number}.loyaltyProgram.pointsExpiration.expiringPointsPercentage`
      | `programInSites.${number}.loyaltyProgram.premiumFeatures.loyaltyProgram`
      | `programInSites.${number}.loyaltyProgram.premiumFeatures.tiers`
      | `programInSites.${number}.loyaltyProgram.premiumFeatures.pointsExpiration`,
      6
    >
  >;
}

export function updateLoyaltyProgram(
  httpClient: HttpClient
): UpdateLoyaltyProgramSignature {
  return (loyaltyProgram: LoyaltyProgram) =>
    universalUpdateLoyaltyProgram(
      loyaltyProgram,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateLoyaltyProgramSignature {
  /**
   * Updates a site's loyalty program.
   *
   * This method updates the name of the loyalty program and the details of the collectible points unit.
   * To activate the loyalty program, call Activate Loyalty Program.
   * @param - Loyalty program fields to update.
   */
  (loyaltyProgram: LoyaltyProgram): Promise<
    NonNullablePaths<
      UpdateLoyaltyProgramResponse,
      | `loyaltyProgram.status`
      | `loyaltyProgram.pointsExpiration.status`
      | `loyaltyProgram.pointsExpiration.monthsOfInactivity`
      | `loyaltyProgram.pointsExpiration.expiringPointsPercentage`
      | `loyaltyProgram.premiumFeatures.loyaltyProgram`
      | `loyaltyProgram.premiumFeatures.tiers`
      | `loyaltyProgram.premiumFeatures.pointsExpiration`,
      4
    >
  >;
}

export function activateLoyaltyProgram(
  httpClient: HttpClient
): ActivateLoyaltyProgramSignature {
  return () =>
    universalActivateLoyaltyProgram(
      // @ts-ignore
      { httpClient }
    );
}

interface ActivateLoyaltyProgramSignature {
  /**
   * Activates a loyalty program.
   *
   * Initially, when a loyalty program is installed, the status is set to `"DRAFT"`.
   * You can change the program's status to `"ACTIVE"` with this method or through the [dashboard](https://www.wix.com/my-account/site-selector/?buttonText=Select%20Site&title=Select%20a%20Site&autoSelectOnSingleSite=true&actionUrl=https:%2F%2Fwww.wix.com%2Fdashboard%2F%7B%7BmetaSiteId%7D%7D%2Floyalty-accounts/wizard/).
   * A site's customers can only earn or redeem points while the program is `"ACTIVE"`.
   *
   * This method only updates the status of a loyalty program. To make other updates to the program, call Update Loyalty Program.
   *
   * To temporarily pause your loyalty program, call Pause Loyalty Program and see [Pausing Your Loyalty Program](https://support.wix.com/en/article/wix-loyalty-program-pausing-your-loyalty-program) for more information.
   */
  (): Promise<
    NonNullablePaths<
      ActivateLoyaltyProgramResponse,
      | `loyaltyProgram.status`
      | `loyaltyProgram.pointsExpiration.status`
      | `loyaltyProgram.pointsExpiration.monthsOfInactivity`
      | `loyaltyProgram.pointsExpiration.expiringPointsPercentage`
      | `loyaltyProgram.premiumFeatures.loyaltyProgram`
      | `loyaltyProgram.premiumFeatures.tiers`
      | `loyaltyProgram.premiumFeatures.pointsExpiration`,
      4
    >
  >;
}

export function pauseLoyaltyProgram(
  httpClient: HttpClient
): PauseLoyaltyProgramSignature {
  return () =>
    universalPauseLoyaltyProgram(
      // @ts-ignore
      { httpClient }
    );
}

interface PauseLoyaltyProgramSignature {
  /**
   * Changes the program status to `"PAUSED"`.
   *
   * See [Pausing Your Loyalty Program](https://support.wix.com/en/article/wix-loyalty-program-pausing-your-loyalty-program) for more information.
   */
  (): Promise<
    NonNullablePaths<
      PauseLoyaltyProgramResponse,
      | `loyaltyProgram.status`
      | `loyaltyProgram.pointsExpiration.status`
      | `loyaltyProgram.pointsExpiration.monthsOfInactivity`
      | `loyaltyProgram.pointsExpiration.expiringPointsPercentage`
      | `loyaltyProgram.premiumFeatures.loyaltyProgram`
      | `loyaltyProgram.premiumFeatures.tiers`
      | `loyaltyProgram.premiumFeatures.pointsExpiration`,
      4
    >
  >;
}

export function getLoyaltyProgramPremiumFeatures(
  httpClient: HttpClient
): GetLoyaltyProgramPremiumFeaturesSignature {
  return () =>
    universalGetLoyaltyProgramPremiumFeatures(
      // @ts-ignore
      { httpClient }
    );
}

interface GetLoyaltyProgramPremiumFeaturesSignature {
  /**
   * Retrieves information about the available loyalty program premium features.
   */
  (): Promise<
    NonNullablePaths<
      GetLoyaltyProgramPremiumFeaturesResponse,
      `loyaltyProgram` | `tiers` | `pointsExpiration`,
      2
    >
  >;
}

export function enablePointsExpiration(
  httpClient: HttpClient
): EnablePointsExpirationSignature {
  return () =>
    universalEnablePointsExpiration(
      // @ts-ignore
      { httpClient }
    );
}

interface EnablePointsExpirationSignature {
  /**
   * Updates the `pointsExpiration` status to `"ENABLED"`.
   */
  (): Promise<
    NonNullablePaths<
      EnablePointsExpirationResponse,
      | `loyaltyProgram.status`
      | `loyaltyProgram.pointsExpiration.status`
      | `loyaltyProgram.pointsExpiration.monthsOfInactivity`
      | `loyaltyProgram.pointsExpiration.expiringPointsPercentage`
      | `loyaltyProgram.premiumFeatures.loyaltyProgram`
      | `loyaltyProgram.premiumFeatures.tiers`
      | `loyaltyProgram.premiumFeatures.pointsExpiration`,
      4
    >
  >;
}

export function disablePointsExpiration(
  httpClient: HttpClient
): DisablePointsExpirationSignature {
  return () =>
    universalDisablePointsExpiration(
      // @ts-ignore
      { httpClient }
    );
}

interface DisablePointsExpirationSignature {
  /**
   * Updates the `pointsExpiration` status to `"DISABLED"`.
   */
  (): Promise<
    NonNullablePaths<
      DisablePointsExpirationResponse,
      | `loyaltyProgram.status`
      | `loyaltyProgram.pointsExpiration.status`
      | `loyaltyProgram.pointsExpiration.monthsOfInactivity`
      | `loyaltyProgram.pointsExpiration.expiringPointsPercentage`
      | `loyaltyProgram.premiumFeatures.loyaltyProgram`
      | `loyaltyProgram.premiumFeatures.tiers`
      | `loyaltyProgram.premiumFeatures.pointsExpiration`,
      4
    >
  >;
}

export const onProgramUpdated = EventDefinition(
  'wix.loyalty.v1.program_updated',
  true,
  (event: ProgramUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.pointDefinition.icon' }],
        },
      ])
    )
)<ProgramUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  ActivateLoyaltyProgramRequest,
  ActivateLoyaltyProgramResponse,
  AssignedFromFloatingReason,
  BaseEventMetadata,
  BooleanFeature,
  BulkGetLoyaltyProgramRequest,
  BulkGetLoyaltyProgramResponse,
  CancelRequestedReason,
  ContractSwitchClassification,
  ContractSwitchedReason,
  DisablePointsExpirationRequest,
  DisablePointsExpirationResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EnablePointsExpirationRequest,
  EnablePointsExpirationResponse,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  Feature,
  FeatureCancelled,
  FeatureCancelledReasonOneOf,
  FeatureContext,
  FeatureDisabled,
  FeatureDisabledReasonOneOf,
  FeatureEnabled,
  FeatureEnabledReasonOneOf,
  FeatureEvent,
  FeatureEventEventOneOf,
  FeaturePeriod,
  FeatureQuantityInfoOneOf,
  FeatureQuotaUsageChanged,
  FeatureUpdated,
  FeatureUpdatedPreviousQuantityInfoOneOf,
  FeatureUpdatedReasonOneOf,
  FocalPoint,
  GetLoyaltyProgramDescriptionRequest,
  GetLoyaltyProgramDescriptionResponse,
  GetLoyaltyProgramPremiumFeaturesRequest,
  GetLoyaltyProgramPremiumFeaturesResponse,
  GetLoyaltyProgramRequest,
  GetLoyaltyProgramResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  LoyaltyAppInstallation,
  LoyaltyProgram,
  LoyaltyProgramActivated,
  LoyaltyProgramDescriptionUpdated,
  ManualFeatureCreationReason,
  ManualSyncReason,
  MessageEnvelope,
  MigratedFromLegacyReason,
  NewFeatureReason,
  PauseLoyaltyProgramRequest,
  PauseLoyaltyProgramResponse,
  PointDefinition,
  PointsExpiration,
  PointsExpirationChanges,
  PointsExpirationConfigurationChanged,
  PointsExpirationDisabled,
  PointsExpirationEnabled,
  PremiumFeatures,
  ProgramInSite,
  ProgramStatus,
  ProgramUpdatedEnvelope,
  QuotaExhaustedReason,
  QuotaFeature,
  QuotaInfo,
  QuotaInfoEntry,
  ReassignedFromSiteReason,
  ReassignedToAnotherSiteReason,
  ReplacedByAnotherSubscriptionReason,
  RestoreInfo,
  SocialMediaChannel,
  SocialMediaSettings,
  Status,
  TransferredFromAnotherAccountReason,
  TransferredToAnotherAccountReason,
  Type,
  UnAssingedToFloatingReason,
  UpdateLoyaltyProgramDescriptionRequest,
  UpdateLoyaltyProgramDescriptionResponse,
  UpdateLoyaltyProgramRequest,
  UpdateLoyaltyProgramResponse,
  V1Type,
  WebhookIdentityType,
} from './loyalty-v1-program-programs.universal.js';
